/**
 * What a trainer may DO to a movement from this screen, wired: open the creator (new, edit,
 * duplicate), change a cover, delete one of their own — and the one sentence the screen's live
 * region says afterwards.
 *
 * Decides nothing. `features/create-exercise` owns the wizard, the writes and what a delete's answer
 * means; `useTheatre` owns where the card goes. This hook is the seam between the two.
 *
 * ── The creator's request OUTLIVES its `open` ───────────────────────────────────────────────
 * `CreateExerciseDialog` renders its popup unconditionally and draws whatever request it is handed
 * — so closing flips `open` and leaves the request in place, and the contents close WITH the dialog
 * instead of a frame before it. A fresh `opening` number per press is what makes a second opening on
 * the same movement start from its own draft.
 *
 * ── After a save, the card shows the result ─────────────────────────────────────────────────
 * A new movement and a duplicate open their own card — the trainer sees what they made, and the
 * catalogue has already been re-read by the time `onSaved` runs, so the lookup lands. An edit leaves
 * the card where it is, and the card redraws from the re-read catalogue by itself.
 *
 * ── A film that went up is WATCHED until the server has processed it ──────────────────────
 * The save answers as soon as the film is uploaded and confirmed; the transcode runs afterwards.
 * `watchVideoProcessing` polls its status and, once it settles, re-reads the catalogue (so the card
 * and the table pick the film up) and says „gotowy” — or says it could not be processed. A watch
 * whose status read fails takes the note down and re-reads, saying nothing. The watch is stopped
 * when this screen goes away, and a save answering after that (the wizard's upload is aborted as it
 * unmounts) is ignored: no card is opened and no watch started on a screen that is gone.
 *
 * ── What became of the files is SHOWN on the card, not only spoken ─────────────────────────
 * `mediaNote` is the sentence the card's hero draws for its movement until the card closes: the
 * cover's failure (D23), and the film's state — being processed while it is watched, then its
 * failure if it did not go up or could not be processed. A film that became ready clears its part,
 * in the same beat the re-read catalogue puts the film itself in the hero.
 *
 * ── A delete closes the card FIRST, then re-reads ───────────────────────────────────────────
 * The prompt goes, then the card, then the catalogue. `useTheatre` holds the drawn movement past its
 * own disappearance from the catalogue, so the card closes on its own animation rather than being
 * pulled out from under Base UI. A refusal keeps the card: a 409 turns the prompt into the phone's
 * in-use refusal naming the live plans (`exerciseInUseRefusal`, mobile #744), anything else leaves
 * the question standing with the phone's „spróbuj ponownie” (D24).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '~/shared/api/mutator';
import { coverRefusalKey } from '~/shared/media/coverFile';
import { prepareCover } from '~/shared/media/compressCover';

import { exerciseSimilarQueryOptions, type ExerciseDetail } from '~/entities/exercise-library';
import {
  deleteExercise,
  exerciseInUseRefusal,
  invalidateAfterExerciseDelete,
  type ExerciseInUseRefusal,
  invalidateExerciseLibrary,
  markExerciseChecked,
  uploadExerciseCover,
  type CreateExerciseMode,
  type ExerciseCreatorRequest,
  type NoticeSentence,
  type SaveExerciseResult,
  saveNotice,
} from '~/features/create-exercise';
import { contentLanguage } from '~/shared/i18n/contentLanguage';

import type { LiveNotice } from '../model/liveNotice';

import { watchVideoProcessing } from './watchVideoProcessing';

/**
 * The delete prompt: asking, a request that failed and may be asked again, or the movement still in
 * use — with the plans the refusal named. `null` while nothing is being deleted.
 */
export type DeletePrompt =
  | { readonly phase: 'confirm'; readonly exercise: ExerciseDetail }
  | { readonly phase: 'failed'; readonly exercise: ExerciseDetail }
  | {
      readonly phase: 'inUse';
      readonly exercise: ExerciseDetail;
      readonly refusal: ExerciseInUseRefusal;
    };

export interface ExerciseActions {
  readonly creatorOpen: boolean;
  readonly creatorRequest: ExerciseCreatorRequest | null;
  readonly openCreator: (mode: CreateExerciseMode, source: ExerciseDetail | null) => void;
  readonly closeCreator: () => void;
  /**
   * The creator's `finalFocus`: `false` („leave focus alone") when the save opened a card, because
   * the CARD puts focus on its own heading (`takeCardLanding`); otherwise the opener, or `null`.
   * Returning the opener there sent focus to the header's `+` behind the new card, and returning the
   * card's popup lost it to the walk arrow when the duplicate's card re-keyed.
   */
  readonly creatorFinalFocus: () => HTMLElement | null | false;
  /**
   * Whether the card now showing `exerciseId` was opened by a save of this kind, and should land
   * focus on its heading. Consumed on read, so a later walk back to the same card does not.
   */
  readonly takeCardLanding: (exerciseId: number, mode: 'create' | 'duplicate') => boolean;
  /** The card closed: a pending landing and a cover failure it was showing both go with it. */
  readonly cardClosed: () => void;
  /**
   * What the card of `exerciseId` shows in its hero about the files a save sent, or `null`: a cover
   * that failed to upload (spoken alone, a sighted trainer saw only „Brak okładki”, owner,
   * 18 September 2026), and the film being processed or not added.
   */
  readonly mediaNote: (exerciseId: number) => string | null;
  readonly onCreatorSaved: (result: SaveExerciseResult, request: ExerciseCreatorRequest) => void;

  readonly deletePrompt: DeletePrompt | null;
  readonly askDelete: (exercise: ExerciseDetail) => void;
  readonly confirmDelete: () => void;
  readonly cancelDelete: () => void;
  readonly deleting: boolean;

  readonly changeCover: (exerciseId: number, file: File) => void;

  /** „Oznacz jako sprawdzone": the review mark taken off an own movement (R17). */
  readonly markChecked: (exerciseId: number) => void;
  /** The mark is being taken off: the button waits. */
  readonly markingChecked: boolean;
  /** The movement whose mark this screen took off last: its card says so until it closes. */
  readonly checkedId: number | null;
  /** The movement whose mark could not be taken off: its card says so where the confirmation stands. */
  readonly checkFailedId: number | null;

  /**
   * What the screen's live region says about the last act, or `null`. `id` is new per notice, so the
   * same sentence twice is still two announcements.
   */
  readonly notice: LiveNotice | null;
}

/** A sentence the card of one movement shows. */
interface MovementNote {
  readonly exerciseId: number;
  readonly text: string;
}

export interface ExerciseActionsInput {
  /** Open the card on a movement — pushes the address. */
  readonly openCard: (exerciseId: number) => void;
  /** Swap the open card to another movement — replaces the address. */
  readonly swapCard: (exerciseId: number) => void;
  /** Close the card because its movement is gone; it chooses where focus lands. */
  readonly closeCardAfterRemoval: () => void;
}

export function useExerciseActions(input: ExerciseActionsInput): ExerciseActions {
  const { t } = useTranslation();
  const client = useQueryClient();

  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorRequest, setCreatorRequest] = useState<ExerciseCreatorRequest | null>(null);
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  /* The card a save opened, until that card has put focus on its heading. A ref, written in the
   * save's handler and consumed where focus is placed — never read during render. */
  const cardLanding = useRef<{ readonly id: number; readonly mode: 'create' | 'duplicate' } | null>(
    null,
  );
  /* Whether the creator's LAST close was a save that opened a card. Separate from `cardLanding`,
   * which the card consumes as soon as it lands — often before the creator's exit animation ends
   * and its `finalFocus` runs, which would then send focus back to the ⋯. */
  const closedToCard = useRef(false);
  /* Which opening a save must belong to. Written only in handlers, read only in the save's
   * callback: a late answer from a wizard the trainer has since replaced must not close the new one
   * (and lose its draft) or open a card for a movement they believe they cancelled. */
  const openingNow = useRef<number | null>(null);
  const [deletePrompt, setDeletePrompt] = useState<DeletePrompt | null>(null);
  const [notice, setNoticeState] = useState<LiveNotice | null>(null);
  /* The card's two notes, each for one movement. Only ever one movement's at a time: a save sets
   * both for the movement it saved, and the card closing clears both. */
  const [coverFailure, setCoverFailure] = useState<MovementNote | null>(null);
  const [filmNote, setFilmNote] = useState<MovementNote | null>(null);
  const say = (sentence: NoticeSentence) =>
    t(sentence.key, sentence.reasonKey === undefined ? {} : { reason: t(sentence.reasonKey) });
  const setNotice = (text: string) => {
    setNoticeState((previous) => ({ id: (previous?.id ?? 0) + 1, text }));
  };
  /* The stop switches of the films still being processed. Written in the save's handler and in
   * each watch's own settlement; the cleanup below stops whatever is left when the screen goes. */
  const processingWatches = useRef(new Set<() => void>());
  useEffect(() => {
    const watches = processingWatches.current;
    return () => {
      for (const stop of watches) stop();
      watches.clear();
      // A save still settling belongs to no opening now: `onCreatorSaved` ignores it.
      openingNow.current = null;
    };
  }, []);
  // Replaces the film's note only while it still describes THIS movement: the trainer may have
  // closed the card, or saved another film since.
  const settleFilmNote = (exerciseId: number, text: string | null) => {
    setFilmNote((current) =>
      current?.exerciseId !== exerciseId ? current : text === null ? null : { exerciseId, text },
    );
  };
  const watchFilm = (exerciseId: number, mediaId: number) => {
    setFilmNote({ exerciseId, text: t('web:exerciseLibrary.theatre.filmProcessing') });
    const stop = watchVideoProcessing(client, mediaId, (outcome) => {
      processingWatches.current.delete(stop);
      if (outcome === 'failed') {
        const text = t('web:exerciseLibrary.creator.notice.videoProcessingFailed');
        settleFilmNote(exerciseId, text);
        setNotice(text);
        return;
      }
      void invalidateExerciseLibrary(client).then(() => {
        settleFilmNote(exerciseId, null);
        if (outcome === 'ready') setNotice(t('web:exerciseLibrary.creator.notice.videoReady'));
      });
    });
    processingWatches.current.add(stop);
  };

  const remove = useMutation({
    mutationFn: async (exercise: ExerciseDetail) => {
      await deleteExercise(exercise.id);
    },
    onSuccess: async (_, exercise) => {
      setDeletePrompt(null);
      input.closeCardAfterRemoval();
      // Dropped BEFORE the invalidation: the card's `/similar` observer is still attached until React
      // re-renders the closed card, so invalidating first refetched the deleted movement into a 404.
      // The CONTENT language, which is what `useTheatre` read that entry under. Keyed by the
      // interface language it dropped a key nobody had written, and the invalidation below then
      // refetched the deleted movement into a 404 on every Polish app reading an English library.
      client.removeQueries({
        queryKey: exerciseSimilarQueryOptions(exercise.id, contentLanguage()).queryKey,
      });
      await invalidateAfterExerciseDelete(client);
      setNotice(t('web:exerciseLibrary.creator.notice.deleted'));
    },
    // The phone's rule: a 409 is the movement still held by a live plan — read for the plans it
    // names — and anything else (a 500, a dead connection) leaves the question standing with the
    // phone's „spróbuj ponownie”. The model gets the status and the problem body, never the error.
    onError: (error: unknown, exercise) => {
      const refusal =
        error instanceof ApiError ? exerciseInUseRefusal(error.status, error.problem) : null;
      setDeletePrompt(
        refusal === null ? { phase: 'failed', exercise } : { phase: 'inUse', exercise, refusal },
      );
    },
  });

  const cover = useMutation({
    mutationFn: async (command: { readonly exerciseId: number; readonly file: File }) => {
      await uploadExerciseCover(command.exerciseId, command.file);
      await invalidateExerciseLibrary(client);
    },
    onSuccess: () => {
      setCoverFailure(null);
      setNotice(t('web:exerciseLibrary.creator.notice.coverChanged'));
    },
    onError: (_error: unknown, command) => {
      const text = t('web:exerciseLibrary.creator.notice.coverChangeFailed');
      setNotice(text);
      setCoverFailure({ exerciseId: command.exerciseId, text });
    },
  });

  const [checkedId, setCheckedId] = useState<number | null>(null);
  const [checkFailedId, setCheckFailedId] = useState<number | null>(null);
  const review = useMutation({
    mutationFn: async (exerciseId: number) => {
      await markExerciseChecked(exerciseId);
      // Awaited, so the button goes with the mark rather than a beat after it.
      await invalidateExerciseLibrary(client);
    },
    onSuccess: (_, exerciseId) => {
      setCheckedId(exerciseId);
      setNotice(t('web:exerciseLibrary.review.checked'));
    },
    // Said in the live region AND drawn on the card, as a failed cover is: a sighted trainer who
    // pressed the button sees why the mark is still there.
    onError: (_, exerciseId) => {
      setCheckFailedId(exerciseId);
      setNotice(t('web:exerciseLibrary.review.markFailed'));
    },
  });

  return {
    creatorOpen,
    creatorRequest,
    openCreator: (mode, source) => {
      // Read in the press's own handler, where the control pressed is still the active element.
      setOpener(document.activeElement instanceof HTMLElement ? document.activeElement : null);
      cardLanding.current = null;
      closedToCard.current = false;
      const opening = (creatorRequest?.opening ?? 0) + 1;
      openingNow.current = opening;
      setCreatorRequest({ mode, source, opening });
      setCreatorOpen(true);
    },
    closeCreator: () => {
      openingNow.current = null;
      setCreatorOpen(false);
    },
    creatorFinalFocus: () =>
      closedToCard.current ? false : opener?.isConnected === true ? opener : null,
    cardClosed: () => {
      cardLanding.current = null;
      setCoverFailure(null);
      setFilmNote(null);
      setCheckedId(null);
      setCheckFailedId(null);
    },
    mediaNote: (exerciseId) => {
      const notes = [coverFailure, filmNote].filter(
        (note): note is MovementNote => note?.exerciseId === exerciseId,
      );
      return notes.length === 0 ? null : notes.map((note) => note.text).join(' ');
    },
    takeCardLanding: (exerciseId, mode) => {
      // Dropped by any OTHER card that asks — one opened or walked to before this landing happened —
      // so it cannot fire on a later, unrelated opening of that movement. The same card asking in the
      // other mode leaves it: a new card's mount effect asks for 'duplicate' a beat BEFORE Base UI's
      // `initialFocus` asks for 'create'.
      const landing = cardLanding.current;
      if (landing === null) return false;
      if (landing.id !== exerciseId) {
        cardLanding.current = null;
        return false;
      }
      if (landing.mode !== mode) return false;
      cardLanding.current = null;
      return true;
    },
    onCreatorSaved: (result, request) => {
      if (openingNow.current !== request.opening) return;
      openingNow.current = null;
      // A new movement and a duplicate both open their own card, and focus belongs on its heading.
      cardLanding.current =
        request.mode === 'edit' ? null : { id: result.exerciseId, mode: request.mode };
      closedToCard.current = request.mode !== 'edit';
      setCreatorOpen(false);
      // The phone's own sentences: the movement IS saved, and a file is the part that failed.
      const said = saveNotice(request.mode, result);
      setNotice(said.sentences.map(say).join(' '));
      setCoverFailure(
        said.coverFailureKey === null
          ? null
          : { exerciseId: result.exerciseId, text: t(said.coverFailureKey) },
      );
      setFilmNote(
        said.filmNote === null ? null : { exerciseId: result.exerciseId, text: say(said.filmNote) },
      );
      if (result.videoUpload.status === 'sent') {
        watchFilm(result.exerciseId, result.videoUpload.mediaId);
      }
      if (request.mode === 'create') input.openCard(result.exerciseId);
      if (request.mode === 'duplicate') input.swapCard(result.exerciseId);
    },

    deletePrompt,
    askDelete: (exercise) => {
      setDeletePrompt({ phase: 'confirm', exercise });
    },
    confirmDelete: () => {
      if (deletePrompt === null) return;
      if (deletePrompt.phase === 'inUse') {
        setDeletePrompt(null);
        return;
      }
      remove.mutate(deletePrompt.exercise);
    },
    cancelDelete: () => {
      if (!remove.isPending) setDeletePrompt(null);
    },
    deleting: remove.isPending,

    markChecked: (exerciseId) => {
      if (review.isPending) return;
      setCheckFailedId(null);
      review.mutate(exerciseId);
    },
    markingChecked: review.isPending,
    checkedId,
    checkFailedId,
    changeCover: (exerciseId, file) => {
      // The same re-encoding the wizard's drop zone applies: the backend reads only JPEG and WebP.
      void prepareCover(file).then((prepared) => {
        if (prepared.ok) cover.mutate({ exerciseId, file: prepared.file });
        else setNotice(t(coverRefusalKey(prepared.refusal)));
      });
    },

    notice,
  };
}
